// Read-only accessors for the guided_report snapshot persisted by the backend
// at the report top level (json_params.guided_report). The report model arrives
// as an Immutable Map with guided_report as a top-level key.
import { Map as IMap } from 'immutable'

type AnyMap = IMap<string, unknown> | null | undefined

// Note: the report name is NOT part of the guided_report snapshot — the backend
// (Api::Skipper::Report::Create#build_guided_snapshot) persists only resolved_report_spec/
// narrative_md/state_md/targeting/generation_id/session/plan/display_md. The display
// name comes from the StatReport title, passed in separately by callers.
export interface ResolvedSpec {
  objectives: string[]
  researchQuestions: string[]
  kpis: string[]
  confidenceLevel: number | null
  methodology: string | null
}

const EMPTY: ResolvedSpec = {
  objectives: [],
  researchQuestions: [],
  kpis: [],
  confidenceLevel: null,
  methodology: null
}

const toArray = (v: unknown): unknown[] => {
  if (v == null) return []
  const maybe = v as { toJS?: () => unknown }
  const plain = typeof maybe.toJS === 'function' ? maybe.toJS() : v
  return Array.isArray(plain) ? plain : []
}

// Every Skipper Report, whatever its provenance and whether or not it kept a snapshot.
export const isSkipperReport = (report: AnyMap): report is IMap<string, unknown> =>
  Boolean(report && typeof report.get === 'function' && 'skipper' == report.get('type'))

// Provenance, not the snapshot: a failed generation drops guided_report, and only the
// auto-launched report belongs under Automated Reports.
export const isSkipperGuidedReport = (report: AnyMap): boolean =>
  isSkipperReport(report) && 'auto_launch' != report.get('origin')

export const isSkipperGuidedDraftReport = (item: AnyMap): boolean =>
  Boolean('draft' == item.get('status') && isSkipperGuidedReport(item))

// A guided row is unsettled until its generation reaches a terminal outcome — before dispatch
// ('draft') and while it runs ('generating'). The popup withholds the same editable and shareable
// content across both: there is nothing to share yet, and an edit racing the generation's own
// writes to the row is refused on a stale edit stamp.
export const isSkipperGuidedUnsettledReport = (item: AnyMap): boolean =>
  Boolean(isSkipperGuidedReport(item) && ['draft', 'generating'].includes(item.get('status') as string))

// The guided-reports feature is "on" (purchased) when it is neither hidden nor in
// an upsell state (available-to-purchase / locked) — the same condition under
// which SkipperGuidedReportButton renders its Create button.
export const isSkipperReportPurchased = (
  feature?: { hidden?: boolean; available?: boolean; disabled?: boolean } | null
): boolean => Boolean(feature && !feature.hidden && !feature.available && !feature.disabled)

export const readResolvedSpec = (report: AnyMap): ResolvedSpec => {
  if (!isSkipperReport(report)) return { ...EMPTY }

  // A failed generation drops the snapshot while the report stays a Skipper Report
  const guided = (report.get('guided_report') as IMap<string, unknown>) || IMap()
  const spec = (guided.get('resolved_report_spec') as IMap<string, unknown>) || IMap()
  const metadata = (spec.get('metadata') as IMap<string, unknown>) || IMap()

  const rqs = toArray(spec.get('research_questions')).map(rq => {
    const item = rq as { text?: string }
    return item && typeof item === 'object' ? item.text || '' : String(rq)
  })

  return {
    objectives: toArray(spec.get('objectives')).map(String),
    researchQuestions: rqs.filter(Boolean),
    kpis: toArray(spec.get('kpis')).map(String),
    confidenceLevel: (spec.get('confidence_level') as number) ?? null,
    methodology: (metadata.get('methodology') as string) ?? null
  }
}

// The plan's derived panel string. Absent on pre-plan reports, which keep the resolved-spec
// rows — no report_prompt fallback, since on that path the field is the raw state.md.
export const readDisplayMd = (report: AnyMap): string => {
  if (!isSkipperReport(report)) return ''

  const guided = (report.get('guided_report') as IMap<string, unknown>) || IMap()
  const displayMd = guided.get('display_md')
  return 'string' == typeof displayMd ? displayMd : ''
}
